import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

// tslint:disable:no-require-imports variable-declaration
declare const require: (module: string) => any;
const jsPDF = require('jspdf');
require('jspdf-autotable');

export interface ExportColumn {
  header: string;
  key: string;
  fmt?: (val: any, row?: any) => string;
}

export interface ExportConfig {
  title: string;
  columns: ExportColumn[];
}

@Injectable({ providedIn: 'root' })
export class ExportService {

  // ── Static column configs per entity ──────────────────────

  static QUOTES: ExportConfig = {
    title: 'Insurance Quotes',
    columns: [
      { header: '#',              key: '__index' },
      { header: 'Policy No.',     key: 'policy_sequence',     fmt: function(v) { return v || '\u2014'; } },
      { header: 'Customer Name',  key: '__name',              fmt: function(_, r) { return ((r.customer_first_name || '') + ' ' + (r.customer_last_name || '')).trim() || '\u2014'; } },
      { header: 'Phone',          key: 'customer_phone',      fmt: function(v) { return v || '\u2014'; } },
      { header: 'Product',        key: 'product_description', fmt: function(v) { return v || '\u2014'; } },
      { header: 'Duration',       key: 'duration_type',       fmt: function(v) { return v === 'annual' ? 'One Year (365)' : (v || '\u2014'); } },
      { header: 'Premium',        key: 'total_premium',       fmt: function(v, r) { return v != null ? (r.currency_code || 'GHS') + ' ' + Number(v).toFixed(2) : '\u2014'; } },
      { header: 'Inception Date', key: 'inception_date',      fmt: function(v) { return v || '\u2014'; } },
      { header: 'Expiry Date',    key: 'expiry_date',         fmt: function(v) { return v || '\u2014'; } },
      { header: 'Date Created',   key: 'date_created',        fmt: function(v) { return v || '\u2014'; } },
    ]
  };

  static POLICIES: ExportConfig = {
    title: 'Insurance Policies',
    columns: [
      { header: '#',              key: '__index' },
      { header: 'Policy No.',     key: 'policy_sequence',     fmt: function(v) { return v || '\u2014'; } },
      { header: 'Customer Name',  key: '__name',              fmt: function(_, r) { return ((r.customer_first_name || '') + ' ' + (r.customer_last_name || '')).trim() || '\u2014'; } },
      { header: 'Product',        key: 'product_description', fmt: function(v) { return v || '\u2014'; } },
      { header: 'Duration',       key: 'duration_type',       fmt: function(v) { return v === 'annual' ? 'One Year (365)' : (v || '\u2014'); } },
      { header: 'Premium',        key: 'total_premium',       fmt: function(v, r) { return v != null ? (r.currency_code || 'GHS') + ' ' + Number(v).toFixed(2) : '\u2014'; } },
      { header: 'Inception Date', key: 'inception_date',      fmt: function(v) { return v || '\u2014'; } },
      { header: 'Expiry Date',    key: 'expiry_date',         fmt: function(v) { return v || '\u2014'; } },
      { header: 'Last Updated',   key: 'date_updated',        fmt: function(v) { return v || '\u2014'; } },
    ]
  };

  static TRANSACTIONS: ExportConfig = {
    title: 'Payment Transactions',
    columns: [
      { header: '#',              key: '__index' },
      { header: 'Transaction ID', key: 'transaction_id',   fmt: function(v) { return v || '\u2014'; } },
      { header: 'Invoice No.',    key: 'invoice_no',        fmt: function(v) { return v || '\u2014'; } },
      { header: 'Customer Name',  key: 'first_name',        fmt: function(v) { return v || '\u2014'; } },
      { header: 'Email',          key: 'email',             fmt: function(v) { return v || '\u2014'; } },
      { header: 'Phone',          key: 'mobile_no',         fmt: function(v) { return v || '\u2014'; } },
      { header: 'Type',           key: 'transaction_type',  fmt: function(v) { return v || '\u2014'; } },
      { header: 'Amount (GHS)',   key: 'amount',            fmt: function(v) { return v != null ? 'GHS ' + Number(v).toFixed(2) : '\u2014'; } },
      { header: 'Status',         key: 'status',            fmt: function(v) { return v || '\u2014'; } },
      { header: 'Date',           key: '__date',            fmt: function(_, r) { return r.date_updatedd || r.date_createdd || '\u2014'; } },
    ]
  };

  static CLIENTS: ExportConfig = {
    title: 'Client Management',
    columns: [
      { header: '#',              key: '__index' },
      { header: 'Client ID',      key: 'clientId',           fmt: function(v) { return v || '\u2014'; } },
      { header: 'Full Name',      key: '__name',             fmt: function(_, r) { return r.fullName || ((r.otherNames || '') + ' ' + (r.surname || '')).trim() || '\u2014'; } },
      { header: 'Email',          key: 'email',              fmt: function(v) { return v || '\u2014'; } },
      { header: 'Phone',          key: 'telephone',          fmt: function(v) { return v || '\u2014'; } },
      { header: 'GH Card No.',    key: 'ghCardNumber',       fmt: function(v) { return v || '\u2014'; } },
      { header: 'Verification',   key: 'verificationStatus', fmt: function(v) { return v || '\u2014'; } },
      { header: 'Status',         key: 'status',             fmt: function(v) { return v || '\u2014'; } },
      { header: 'Date Onboarded', key: 'createdAt',          fmt: function(v) { return v ? new Date(v).toLocaleDateString('en-GB') : '\u2014'; } },
    ]
  };

  static USERS: ExportConfig = {
    title: 'User Management',
    columns: [
      { header: '#',         key: '__index' },
      { header: 'User ID',   key: 'userID',    fmt: function(v) { return v || '\u2014'; } },
      { header: 'Full Name', key: 'fullName',  fmt: function(v) { return v || '\u2014'; } },
      { header: 'Email',     key: 'email',     fmt: function(v) { return v || '\u2014'; } },
      { header: 'Phone',     key: 'phone',     fmt: function(v) { return v || '\u2014'; } },
      { header: 'User Type', key: 'userType',  fmt: function(v) { return v || '\u2014'; } },
      { header: 'Bank',      key: '__bank',    fmt: function(_, r) { return (r.bank && r.bank.bankName) ? r.bank.bankName : '\u2014'; } },
      { header: 'Status',    key: 'status',    fmt: function(v) { return v || '\u2014'; } },
    ]
  };

  // ── Core helpers ──────────────────────────────────────────

  private toRows(columns: ExportColumn[], data: any[]): string[][] {
    return data.map(function(row, i) {
      return columns.map(function(col) {
        if (col.key === '__index') { return String(i + 1); }
        const isComputed = col.key.length > 2 && col.key.charAt(0) === '_' && col.key.charAt(1) === '_';
        const val = isComputed ? null : row[col.key];
        return col.fmt ? col.fmt(val, row) : (val != null ? String(val) : '\u2014');
      });
    });
  }

  private getHeaders(columns: ExportColumn[]): string[] {
    return columns.map(function(c) { return c.header; });
  }

  // ── Public export methods ─────────────────────────────────

  exportToExcel(config: ExportConfig, data: any[], filename: string): void {
    const headers = this.getHeaders(config.columns);
    const allRows: any[][] = [headers].concat(this.toRows(config.columns, data));
    const ws = XLSX.utils.aoa_to_sheet(allRows);
    ws['!cols'] = config.columns.map(function(c) { return { wch: Math.max(c.header.length + 4, 16) }; });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.title);
    XLSX.writeFile(wb, filename + '.xlsx');
  }

  exportToCSV(config: ExportConfig, data: any[], filename: string): void {
    const headers = this.getHeaders(config.columns);
    const allRows: any[][] = [headers].concat(this.toRows(config.columns, data));
    const ws = XLSX.utils.aoa_to_sheet(allRows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  exportToPDF(config: ExportConfig, data: any[], filename: string): void {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const headers = this.getHeaders(config.columns);
    const rows = this.toRows(config.columns, data);

    doc.setFontSize(20);
    doc.setTextColor(107, 45, 139);
    doc.text(config.title, 14, 20);

    doc.setFontSize(9);
    doc.setTextColor(110, 110, 130);
    const generated = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text('Generated: ' + generated + '   |   Total records: ' + data.length, 14, 27);

    doc.setDrawColor(107, 45, 139);
    doc.setLineWidth(0.4);
    doc.line(14, 30, 283, 30);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 34,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        textColor: [40, 40, 54],
        lineColor: [228, 228, 235],
        lineWidth: 0.2,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [107, 45, 139],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8.5,
      },
      alternateRowStyles: { fillColor: [244, 240, 248] },
      margin: { left: 14, right: 14 },
    });

    doc.save(filename + '.pdf');
  }

  export(format: 'excel' | 'csv' | 'pdf', config: ExportConfig, data: any[], filename: string): void {
    if (format === 'excel') { this.exportToExcel(config, data, filename); }
    else if (format === 'csv') { this.exportToCSV(config, data, filename); }
    else if (format === 'pdf') { this.exportToPDF(config, data, filename); }
  }
}
